import React, { ChangeEvent, useEffect, useMemo } from 'react';
import { InfluencerAdditionalItemsTable } from './InfluencerAdditionalItemsTable';
import { InfluencerContractPlatformTable } from './InfluencerContractPlatformTable';
import { Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { InfluencerContractOptionalItemsTable } from './InfluencerContractOptionalItemsTable';
import _debounce from 'lodash/debounce';
import { useDispatch, useSelector } from 'react-redux';
import {
    setPersonalOfferDescription,
    setInviteAttachment,
} from '../../../../../../../store/influencer/contractInfluencerSlice';
import { IAllStates } from '../../../../../../../store/rootReducer';
import { DeclineOffer } from '../../../offer-modal/tabs/offer/DeclineOffer';
import { InfluencerOfferHeader } from '../../../offer-modal/tabs/offer/InfluencerOfferHeader';
import { Totals } from '../../../../../../brand/campaign/book/offer/modal/tabs/offer/Totals';
import {
    calcFee,
    downloadFile,
    formatDate,
    getSubtotalAdditionalItems,
    getSubtotalOfferItems,
} from '../../../../../../../shared/functions/Functions';
import { TotalTypes } from '../../../../../../../enum/TotalTypes';
import { InfluencerFeeTotals } from '../../../../../../../shared/components/offer/InfluencerFeeTotals';
import { UploadImage } from '../../../../../../../shared/components/UploadImage';
import FilePlaceholder from '../../../../../../../shared/components/placeholder/FilePlaceholder';
import { Fancybox } from '../../../../../../../shared/components/Fancybox';
import { Fancybox as NativeFancybox } from '@fancyapps/ui';
import { getTruncatedData } from '../../../../../../../utils/helpers/getTruncatedData';
import TooltipOverlay from '../../../../../../../shared/components/tooltip/Tooltip';

export function InfluencerContractTab() {
    const { t } = useTranslation();
    const {
        initialContract,
        isEditable,
        campaign,
        contractModalStatus,
        isCharity,
        feePercentage,
        hasFee,
        isIvConnectedClub,
        inviteAttachment,
    } = useSelector((state: IAllStates) => state.contractInfluencer);
    const dispatch = useDispatch();

    const additionalItemsSum = useMemo(
        () => getSubtotalAdditionalItems(initialContract.distribution),
        [initialContract.distribution]
    );

    const contractItemsSum = useMemo(
        () => getSubtotalOfferItems(initialContract.contractItems),
        [initialContract.contractItems]
    );
    const legalDocument = campaign?.contracts[0]?.legalDocuments?.[0];
    const { truncatedName: truncatedFileName, isTruncated: isFileNameTruncated } = getTruncatedData(
        legalDocument?.originalName ?? '',
        30
    );
    const { truncatedName: truncatedUploaderName, isTruncated: isUploaderNameTruncated } = getTruncatedData(
        legalDocument?.uploadedBy?.displayName ?? '',
        20
    );

    const displayFiles =
        inviteAttachment !== undefined
            ? inviteAttachment
            : contractModalStatus === 'negotiate' && legalDocument
            ? [
                  {
                      name: legalDocument.originalName ?? legalDocument.name,
                      type: legalDocument.mime,
                  } as unknown as Blob,
              ]
            : undefined;

    useEffect(() => {
        if (contractModalStatus === 'negotiate' && legalDocument && inviteAttachment === undefined) {
            dispatch(
                setInviteAttachment([
                    {
                        name: legalDocument.originalName ?? legalDocument.name,
                        type: legalDocument.mime,
                    } as unknown as Blob,
                ])
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contractModalStatus, legalDocument?.id]);

    const { serviceFeeSum, grandTotalSum, subTotalAndAddons } = useMemo(() => {
        const baseTotal = additionalItemsSum + contractItemsSum;
        const fee = hasFee ? calcFee(baseTotal, feePercentage) : 0;
        return {
            serviceFeeSum: Math.round(fee),
            grandTotalSum: baseTotal - Math.round(fee),
            subTotalAndAddons: baseTotal,
        };
    }, [additionalItemsSum, contractItemsSum, hasFee, feePercentage, isIvConnectedClub]);

    const changeHandler = _debounce((event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const value = event?.target?.value;
        dispatch(setPersonalOfferDescription(value));
    }, 300);

    const saveInviteFile: React.Dispatch<React.SetStateAction<Blob[] | MediaSource[] | undefined>> = value => {
        const resolvedFiles = typeof value === 'function' ? value(inviteAttachment) : value;
        const normalizedFiles = Array.isArray(resolvedFiles)
            ? resolvedFiles
            : resolvedFiles
            ? [resolvedFiles]
            : undefined;
        dispatch(setInviteAttachment(normalizedFiles as Blob[] | MediaSource[] | undefined));
    };

    return (
        <>
            <DeclineOffer
                declineComment={campaign?.contracts[0]?.declineComment}
                declineReason={campaign?.contracts[0]?.declineReason}
                status={campaign?.contracts[0]?.status}
                cancellationNote={campaign?.contracts[0]?.cancellationNote}
                cancellationDocumentName={campaign?.contracts[0]?.cancellationDocumentName}
                cancellationDocumentPath={campaign?.contracts[0]?.cancellationDocumentPath}
                cancellationDocumentUploadedAt={campaign?.contracts[0]?.cancellationDocumentUploadedAt}
            />
            <InfluencerOfferHeader
                createdAt={campaign?.contracts[0]?.createdAt ?? ''}
                offerModalStatus={contractModalStatus}
                isEditable={isEditable}
                isRequested={campaign?.contracts[0]?.isRequested}
                status={campaign?.contracts[0]?.status}
                acceptedDate={campaign?.contracts[0]?.acceptedDate ?? ''}
            />
            <div className="py-md-3">
                <InfluencerContractPlatformTable />
                <InfluencerAdditionalItemsTable />
                <div className="w-100 my-2">
                    <Totals price={additionalItemsSum} isCharity={isCharity} title={TotalTypes.ADDITIONAL_ITEMS} />
                </div>
                {(!isCharity || (isCharity && grandTotalSum > 0)) && (
                    <InfluencerFeeTotals
                        subTotalAndAddons={subTotalAndAddons}
                        serviceFeeSum={serviceFeeSum}
                        grandTotalSum={grandTotalSum}
                        feePercentage={feePercentage}
                        hasFee={hasFee}
                        isIvConnectedClub={isIvConnectedClub}
                    />
                )}
                {contractModalStatus === 'negotiate' &&
                    campaign?.contractPlaceholder?.distribution &&
                    campaign?.contractPlaceholder?.distribution?.length > 0 &&
                    !isCharity && <InfluencerContractOptionalItemsTable />}
                {contractModalStatus === 'negotiate' && (
                    <>
                        <h6 className="mt-3">{t('influencer.offer_management.modal.add_personal_offer')}</h6>
                        <div className="row">
                            <div className="col-md-6">
                                <Form.Control
                                    id="basic-textarea"
                                    onChange={changeHandler}
                                    defaultValue={initialContract.personalOffer}
                                    as="textarea"
                                    name="personalOffer"
                                    rows={5}
                                    disabled={campaign?.isCharity}
                                    placeholder={t('influencer.offer_management.modal.add_personal_offer_placeholder')}
                                />
                            </div>
                            <div className="col-md-6 mt-3 mt-md-0">
                                <div className="offer-dropzone">
                                    <UploadImage
                                        saveFile={saveInviteFile}
                                        isMultiple={false}
                                        initialFiles={displayFiles}
                                        isFullHeight={true}
                                        acceptedFormats="application/pdf"
                                        centerText={true}
                                        fileName={true}
                                    />
                                </div>
                            </div>
                        </div>
                    </>
                )}
                {contractModalStatus !== 'negotiate' && campaign?.contracts[0]?.personalOffer && (
                    <div className="mt-4 px-2">
                        <div>
                            <h6>{t('influencer.offer_management.modal.add_personal_offer')}</h6>
                        </div>
                        <p className="text-muted mb-0">{campaign?.contracts[0]?.personalOffer}</p>
                    </div>
                )}
                {contractModalStatus !== 'negotiate' && campaign?.contracts[0]?.legalDocuments?.[0] && (
                    <div className="mt-3 px-1">
                        <div
                            className="d-flex align-items-start p-2 border rounded position-relative"
                            style={{ maxWidth: 300 }}
                        >
                            <Fancybox>
                                <FilePlaceholder
                                    file={campaign?.contracts[0]?.legalDocuments?.[0]}
                                    hideText={true}
                                    classIcon="font-22"
                                    styleContent={{ width: 40, height: 40, flexShrink: 0 }}
                                    nameFancybox="legal-document"
                                    handleDownload={() =>
                                        downloadFile(
                                            campaign?.contracts[0]?.legalDocuments?.[0]?.path ?? '',
                                            campaign?.contracts[0]?.legalDocuments?.[0]?.originalName
                                        )
                                    }
                                    right={-10}
                                />
                            </Fancybox>
                            <div className="ms-2">
                                <TooltipOverlay content={isFileNameTruncated ? legalDocument?.originalName : ''}>
                                    <p
                                        className="mb-0 fw-semibold cursor-pointer"
                                        onClick={() =>
                                            NativeFancybox.show([
                                                {
                                                    src: campaign?.contracts[0]?.legalDocuments?.[0]?.path,
                                                    type: 'iframe',
                                                },
                                            ])
                                        }
                                    >
                                        {truncatedFileName || '-'}
                                    </p>
                                </TooltipOverlay>
                                <p className="mb-0 text-muted fs-12">
                                    {t('influencer.offer_management.modal.uploaded_by')}:{' '}
                                    <TooltipOverlay
                                        content={isUploaderNameTruncated ? legalDocument?.uploadedBy?.displayName : ''}
                                        dFlex={false}
                                    >
                                        <span>{truncatedUploaderName || '-'}</span>
                                    </TooltipOverlay>
                                </p>
                                <p className="mb-0 text-muted fs-12">
                                    {t('influencer.offer_management.modal.uploaded_at')}:{' '}
                                    {formatDate(campaign?.contracts[0]?.legalDocuments?.[0]?.updatedAt)}
                                </p>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
}
